import React, { useState, useEffect, useCallback } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import ExportProgressModal from './ExportProgressModal';
import {
  createExportJob,
  getExportJobStatus,
  downloadExportFile,
  deleteExportJob,
  ExportJob,
  ExportEntityConfig,
} from '../services/genericExportService';

/**
 * Generic Export Button Component
 * Reusable component for exporting data from various entity types (Alerts, Cases, Incidents, etc.)
 */

interface GenericExportButtonProps {
  /**
   * Export entity configuration (exportType and endpoint)
   */
  config: ExportEntityConfig;
  
  /**
   * Filters to apply to the export
   */
  filters: Record<string, any>;
  
  /**
   * Columns to include in the export
   */
  columns?: { key: string; header: string }[];
  
  /**
   * Whether the button should be disabled
   */
  disabled?: boolean;
  
  /**
   * Additional CSS classes
   */
  className?: string;
  
  /**
   * Total number of rows (for display purposes, optional)
   */
  totalRows?: number;
  
  /**
   * Callback fired when export starts
   */
  onExportStart?: () => void;
}

const GenericExportButton: React.FC<GenericExportButtonProps> = ({
  config,
  filters,
  columns,
  disabled = false,
  className = '',
  totalRows: _totalRows,
  onExportStart,
}) => {
  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<ExportJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  // Poll for job status
  useEffect(() => {
    if (!jobId) return;

    const pollInterval = setInterval(async () => {
      try {
        const status = await getExportJobStatus(config, jobId);
        if (status) {
          setJob(status);
          if (status.status === 'COMPLETED' || status.status === 'FAILED' || status.status === 'EXPIRED') {
            clearInterval(pollInterval);
          }
        }
      } catch (err) {
        console.error('Failed to get export status:', err);
      }
    }, 1000); // Poll every second

    return () => clearInterval(pollInterval);
  }, [jobId, config]);

  const handleStartExport = useCallback(async () => {
    setError(null);
    setJob(null);
    setShowModal(true);

    // Call onExportStart callback if provided
    if (onExportStart) {
      onExportStart();
    }

    try {
      const newJobId = await createExportJob(config, {
        filters,
        columns,
      });
      setJobId(newJobId);
    } catch (err: any) {
      setError(err.message || 'Failed to start export');
    }
  }, [config, filters, columns, onExportStart]);

  const handleClose = useCallback(async (skipCleanup = false) => {
    // Cleanup: if job completed but user closed without downloading, delete the file
    if (!skipCleanup && jobId && job?.status === 'COMPLETED') {
      try {
        await deleteExportJob(config, jobId);
      } catch (cleanupErr) {
        // Silently ignore cleanup errors
        console.warn('Failed to cleanup export file:', cleanupErr);
      }
    }
    setShowModal(false);
    setJobId(null);
    setJob(null);
    setError(null);
  }, [config, jobId, job]);

  const handleDownload = useCallback(async () => {
    if (!job?.fileName || !jobId) return;

    try {
      await downloadExportFile(config, jobId, job.fileName, job.downloadUrl);
      // DON'T delete immediately - the browser download is async and needs the blob to exist
      // The file will be cleaned up by:
      // 1. Expiry job (after 24 hours)
      // 2. When user closes modal without downloading
      // Close modal after initiating download (skip cleanup to let download complete)
      handleClose(true);
    } catch (err: any) {
      setError(err.message || 'Failed to download file');
    }
  }, [config, job, jobId, handleClose]);

  return (
    <>
      <button
        onClick={handleStartExport}
        disabled={disabled}
        className={`inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      >
        <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
        Export CSV
      </button>

      <ExportProgressModal
        variant="server"
        open={showModal}
        job={job}
        error={error}
        downloadButtonLabel="Download CSV"
        onDownload={() => void handleDownload()}
        onClose={() => void handleClose()}
      />
    </>
  );
};

export default GenericExportButton;

